import { useMemo } from 'react';

import { Button, OfferCard } from '@/components/Shared/ui';
import InfoCircle from '@/media/svg/info-circle.svg?react';
import { useAppSelector } from '@/store';

import { getOfferBodyProducts } from '../../helpers/offerBodyProduct';
import useSelectOffer from '../../hooks/useSelectOffer';
import type {
  TAlternativeBankOffer,
  TBankOffers
} from '../../types/bank-offer.types';

const OFFER_ACTION_STATUSES = [
  'approved',
  'accepted_alternative',
  'pre_approved',
  'client_accepted'
] as const;

interface IProps {
  alternativeInfo: TAlternativeBankOffer;
  bank: TBankOffers;
  bccBankContracts?: string;
  hasOfferedResponse: boolean;
  headerIcon: string | undefined;
  headerTitle: string | undefined;
  isBlueColoredStatus: boolean;
  isCurrentResponseOffered: boolean;
  isMobile: boolean;
  setOfferDetailsModal: (value: boolean | string) => void;
}

const AlternativeItem: React.FC<IProps> = ({
  alternativeInfo,
  bank,
  bccBankContracts = '',
  hasOfferedResponse,
  headerIcon = '',
  headerTitle = '',
  isBlueColoredStatus,
  isCurrentResponseOffered,
  isMobile,
  setOfferDetailsModal
}) => {
  // Не нужно вызывать useFormulateOfferItem внутри альтернатив.
  // Иначе все расчеты будут повторяться

  const { userAccesses } = useAppSelector((state) => state.userInfo);
  const { banksOffers } = useAppSelector((state) => state.offerStep);
  const { orderApp } = useAppSelector((state) => state.newOrder);

  const { handleSelect, handleNewSelect } = useSelectOffer({ bankOffer: bank });

  const handleAcceptAlternative = (
    bank: TBankOffers,
    alternative?: TAlternativeBankOffer
  ) => {
    if (!alternative) return;

    if (bank.bank.name === 'eu_bank_v2') {
      handleNewSelect({
        ...bank,
        ...alternative,
        responseUuid: bank.id,
        offerId: alternative.extUuid,
        extUuid: alternative.extUuid,
        loanAmount: alternative.loanAmount,
        initialPayment: alternative.initialPayment,
        monthlyPayment: alternative.monthlyPayment,
        rate: alternative.rate,
        loanPeriod: alternative.loanPeriod
      } as TBankOffers | TAlternativeBankOffer);
    } else {
      handleSelect({
        ...bank,
        ...alternative,
        rate: String(alternative.rate),
        loanPeriod: String(alternative.loanPeriod),
        initialPayment: String(alternative.initialPayment),
        extId: alternative.extUuid
      } as TBankOffers);
    }
  };

  const alternativeFooterActions = useMemo(() => {
    const hasClientAccepted =
      banksOffers.some((offer) => offer.status.code === 'client_accepted') &&
      bank.status.code !== 'client_accepted';
    const canAcceptAlternative =
      !orderApp?.isOnlineRequest &&
      userAccesses.autoCredit.canAcceptAlternative;

    if (
      hasClientAccepted ||
      hasOfferedResponse ||
      !OFFER_ACTION_STATUSES.includes(
        bank.status.code as (typeof OFFER_ACTION_STATUSES)[number]
      )
    ) {
      return [];
    }

    const actions: React.ReactElement[] = [];
    actions.push(
      <Button
        key={`${bank.id}-${alternativeInfo.extUuid}`}
        size="small"
        variant="filled-red"
        onClick={() => handleAcceptAlternative(bank, alternativeInfo)}
        disabled={!canAcceptAlternative}
      >
        Принять
      </Button>
    );
    if (alternativeInfo.extUuid === 'susp') {
      actions.push(
        <Button
          key={bank.id + 'description'}
          onClick={() => setOfferDetailsModal(bccBankContracts)}
          size="small"
        >
          <InfoCircle />
          Задолженности
        </Button>
      );
    }

    return actions;
  }, [
    bank,
    alternativeInfo,
    hasOfferedResponse,
    userAccesses,
    banksOffers,
    orderApp?.isOnlineRequest,
    handleAcceptAlternative,
    setOfferDetailsModal,
    bccBankContracts
  ]);

  return (
    <OfferCard
      key={alternativeInfo.extUuid}
      className="bank-item"
      status="success"
      extId={alternativeInfo.extUuid}
      statusText={
        bank.bank.name === 'bcc' && !isBlueColoredStatus
          ? bank.status.name
          : 'Одобрено (Альтернатива)'
      }
    >
      <OfferCard.Header
        title={headerTitle}
        icon={headerIcon}
        headerActions={alternativeFooterActions}
        info={
          hasOfferedResponse && !isCurrentResponseOffered
            ? 'Есть принятое предложение'
            : undefined
        }
      />

      <OfferCard.Body
        footerActions={alternativeFooterActions}
        additionalProducts={
          alternativeInfo.additionalProducts &&
          alternativeInfo.additionalProducts.length > 0
            ? {
                title: 'Доп. продукты',
                content: alternativeInfo.additionalProducts
              }
            : undefined
        }
        products={getOfferBodyProducts({
          monthlyPayment: alternativeInfo.monthlyPayment,
          rate: alternativeInfo.rate,
          loanPeriod: alternativeInfo.loanPeriod,
          loanAmount: alternativeInfo.loanAmount,
          initialPayment: alternativeInfo.initialPayment,
          bankProduct: alternativeInfo.product || bank.product,
          firstMonthlyPayment:
            'firstMonthlyPayment' in alternativeInfo
              ? alternativeInfo.firstMonthlyPayment
              : undefined,
          additionalConditions:
            'additionalConditions' in alternativeInfo
              ? alternativeInfo.additionalConditions
              : undefined,
          isMobile
        })}
      />
    </OfferCard>
  );
};

export default AlternativeItem;
